/** WebAuthn in the browser, without a library (ADR-0105, option P).
 *
 * The server speaks the specification's JSON form: every byte value is a base64url string. The
 * browser API wants `ArrayBuffer`s going in and hands `ArrayBuffer`s back, so this module is the
 * codec between them and a thin wrapper around `navigator.credentials` - about sixty lines of real
 * work, which is why no npm package stands in for it.
 *
 * **Proven against a software authenticator on the server side, against fakes here, and (2026-10-30)
 * against a Chromium virtual authenticator in the browser gate** (`ui-check-passkeys.mjs`). No
 * physical, platform or synced authenticator, and no browser but Chromium, has run this code (the
 * ADR's last section). The
 * newer `PublicKeyCredential.parse...FromJSON` / `toJSON` helpers are deliberately not used: older
 * engines lack them, and the manual conversion below is the part a test can pin.
 *
 * **Nothing here stores anything.** A credential response is passed to the server and forgotten; no
 * `localStorage`, no cache.
 */

/** Why the browser did not give us a credential. */
export type PasskeyFailure =
  /** The person dismissed the prompt, or it timed out. Not an error worth shouting about. */
  | "cancelled"
  /** The authenticator already holds a passkey for this account (the exclude-list matched). */
  | "already_registered"
  /** This browser or context cannot do WebAuthn at all (no secure context, no API). */
  | "unsupported"
  /** Anything else the browser threw. */
  | "failed";

export class PasskeyBrowserError extends Error {
  readonly kind: PasskeyFailure;

  constructor(kind: PasskeyFailure, message: string) {
    super(message);
    this.name = "PasskeyBrowserError";
    this.kind = kind;
  }
}

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** base64url (no padding) to bytes. Throws on anything that is not base64url. */
export function fromBase64Url(text: string): ArrayBuffer {
  if (!/^[A-Za-z0-9_-]*$/.test(text)) throw new Error("not base64url");
  const padded = text.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(text.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

/** Bytes to base64url, no padding. */
export function toBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function bytesField(source: JsonObject, key: string): ArrayBuffer {
  const value = source[key];
  if (typeof value !== "string") throw new Error(`${key} is missing`);
  return fromBase64Url(value);
}

function descriptors(value: unknown): PublicKeyCredentialDescriptor[] {
  if (value === undefined) return [];
  if (!Array.isArray(value)) throw new Error("credential list is not a list");
  return value.map((entry): PublicKeyCredentialDescriptor => {
    if (!isObject(entry)) throw new Error("credential entry is not an object");
    const transports = Array.isArray(entry.transports)
      ? (entry.transports.filter((t): t is string => typeof t === "string") as AuthenticatorTransport[])
      : undefined;
    return {
      type: "public-key",
      id: bytesField(entry, "id"),
      ...(transports ? { transports } : {}),
    };
  });
}

/** The server's registration options as the browser wants them. */
export function creationOptionsFromJson(json: unknown): PublicKeyCredentialCreationOptions {
  if (!isObject(json) || !isObject(json.rp) || !isObject(json.user)) {
    throw new Error("registration options are malformed");
  }
  const { user, rp } = json;
  if (typeof user.name !== "string" || typeof user.displayName !== "string") {
    throw new Error("registration options are malformed");
  }
  if (typeof rp.name !== "string") throw new Error("registration options are malformed");
  const pubKeyCredParams = Array.isArray(json.pubKeyCredParams)
    ? (json.pubKeyCredParams as PublicKeyCredentialParameters[])
    : [];
  const options: PublicKeyCredentialCreationOptions = {
    rp: { name: rp.name, ...(typeof rp.id === "string" ? { id: rp.id } : {}) },
    user: { id: bytesField(user, "id"), name: user.name, displayName: user.displayName },
    challenge: bytesField(json, "challenge"),
    pubKeyCredParams,
    excludeCredentials: descriptors(json.excludeCredentials),
  };
  if (typeof json.timeout === "number") options.timeout = json.timeout;
  if (typeof json.attestation === "string") {
    options.attestation = json.attestation as AttestationConveyancePreference;
  }
  if (isObject(json.authenticatorSelection)) {
    options.authenticatorSelection = json.authenticatorSelection;
  }
  return options;
}

/** The server's sign-in options as the browser wants them. */
export function requestOptionsFromJson(json: unknown): PublicKeyCredentialRequestOptions {
  if (!isObject(json)) throw new Error("sign-in options are malformed");
  const options: PublicKeyCredentialRequestOptions = {
    challenge: bytesField(json, "challenge"),
    allowCredentials: descriptors(json.allowCredentials),
  };
  if (typeof json.timeout === "number") options.timeout = json.timeout;
  if (typeof json.rpId === "string") options.rpId = json.rpId;
  if (typeof json.userVerification === "string") {
    options.userVerification = json.userVerification as UserVerificationRequirement;
  }
  return options;
}

/** What a registration gives the server, in the specification's JSON form. */
export function registrationToJson(credential: PublicKeyCredential): JsonObject {
  const response = credential.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === "function" ? response.getTransports() : ([] as string[]);
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      attestationObject: toBase64Url(response.attestationObject),
      transports,
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    ...(credential.authenticatorAttachment
      ? { authenticatorAttachment: credential.authenticatorAttachment }
      : {}),
  };
}

/** What a sign-in gives the server, in the specification's JSON form. */
export function assertionToJson(credential: PublicKeyCredential): JsonObject {
  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    rawId: toBase64Url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: toBase64Url(response.clientDataJSON),
      authenticatorData: toBase64Url(response.authenticatorData),
      signature: toBase64Url(response.signature),
      ...(response.userHandle && response.userHandle.byteLength > 0
        ? { userHandle: toBase64Url(response.userHandle) }
        : {}),
    },
    clientExtensionResults: credential.getClientExtensionResults(),
    ...(credential.authenticatorAttachment
      ? { authenticatorAttachment: credential.authenticatorAttachment }
      : {}),
  };
}

/** Whether this browser can attempt a passkey at all. A cheap, synchronous test: it does not say a
 * usable authenticator is present, only that asking is possible - the person finds that out when
 * the browser prompts. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential === "function" &&
    typeof navigator !== "undefined" &&
    typeof navigator.credentials?.create === "function" &&
    typeof navigator.credentials?.get === "function"
  );
}

function classify(error: unknown): PasskeyBrowserError {
  if (error instanceof PasskeyBrowserError) return error;
  const name = error instanceof DOMException || error instanceof Error ? error.name : "";
  if (name === "NotAllowedError" || name === "AbortError") {
    return new PasskeyBrowserError("cancelled", "The passkey prompt was dismissed or timed out.");
  }
  if (name === "InvalidStateError") {
    return new PasskeyBrowserError(
      "already_registered",
      "This device already has a passkey for your account.",
    );
  }
  if (name === "NotSupportedError" || name === "SecurityError") {
    return new PasskeyBrowserError("unsupported", "This browser cannot use a passkey here.");
  }
  return new PasskeyBrowserError("failed", "The passkey could not be used.");
}

/** Ask the browser to create a passkey from the server's options; return the JSON to send back. */
export async function createPasskey(optionsJson: unknown): Promise<JsonObject> {
  if (!passkeysSupported()) {
    throw new PasskeyBrowserError("unsupported", "This browser cannot use a passkey here.");
  }
  try {
    const created = await navigator.credentials.create({
      publicKey: creationOptionsFromJson(optionsJson),
    });
    if (!created) throw new PasskeyBrowserError("failed", "The passkey could not be created.");
    return registrationToJson(created as PublicKeyCredential);
  } catch (error) {
    throw classify(error);
  }
}

/** Ask the browser for a passkey assertion from the server's options; return the JSON to send. */
export async function getPasskey(optionsJson: unknown): Promise<JsonObject> {
  if (!passkeysSupported()) {
    throw new PasskeyBrowserError("unsupported", "This browser cannot use a passkey here.");
  }
  try {
    const got = await navigator.credentials.get({
      publicKey: requestOptionsFromJson(optionsJson),
    });
    if (!got) throw new PasskeyBrowserError("failed", "No passkey was returned.");
    return assertionToJson(got as PublicKeyCredential);
  } catch (error) {
    throw classify(error);
  }
}
